import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { Save, Globe, Phone, Mail, MapPin, Clock, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminContact = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const [contactInfo, setContactInfo] = useState({
    company_name: '',
    address: {
      street: '',
      city: '',
      postal_code: '',
      country: ''
    },
    phone: '',
    whatsapp: '',
    email: '',
    working_hours: {
      weekdays: '',
      saturday: '',
      sunday: ''
    },
    social: {
      facebook: '',
      instagram: ''
    },
    map_coordinates: {
      lat: '',
      lng: ''
    }
  });

  useEffect(() => {
    fetchContactSettings();
  }, []);

  const fetchContactSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/api/settings/contact`);
      if (response.ok) {
        const data = await response.json();
        setContactInfo(data);
      }
    } catch (error) {
      console.error('Error fetching contact settings:', error);
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/settings/contact`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(contactInfo)
      });

      if (response.ok) {
        toast.success(t('common.success'));
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error('Error saving contact settings:', error);
      toast.error(t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{t('nav.contact')}</h1>
        <Button onClick={handleSave} disabled={saving} className="bg-orange-500 hover:bg-orange-600">
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('admin.saving')}
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              {t('admin.save')}
            </>
          )}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Company Info */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-orange-500" />
            {t('contact.admin.company_info')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.company_name')}</label>
              <Input
                value={contactInfo.company_name}
                onChange={(e) => setContactInfo(prev => ({ ...prev, company_name: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            {t('contact.admin.address_section')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.street')}</label>
              <Input
                value={contactInfo.address?.street || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  address: { ...prev.address, street: e.target.value }
                }))}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('contact.admin.city')}</label>
                <Input
                  value={contactInfo.address?.city || ''}
                  onChange={(e) => setContactInfo(prev => ({ 
                    ...prev, 
                    address: { ...prev.address, city: e.target.value }
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('contact.admin.postal_code')}</label>
                <Input
                  value={contactInfo.address?.postal_code || ''}
                  onChange={(e) => setContactInfo(prev => ({ 
                    ...prev, 
                    address: { ...prev.address, postal_code: e.target.value }
                  }))}
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.country')}</label>
              <Input
                value={contactInfo.address?.country || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  address: { ...prev.address, country: e.target.value }
                }))}
              />
            </div>
          </div>
        </div>

        {/* Contact Details */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Phone className="h-5 w-5 text-orange-500" />
            {t('contact.admin.contact_details')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.phone')}</label>
              <Input
                value={contactInfo.phone}
                onChange={(e) => setContactInfo(prev => ({ ...prev, phone: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.whatsapp')}</label>
              <Input
                value={contactInfo.whatsapp}
                onChange={(e) => setContactInfo(prev => ({ ...prev, whatsapp: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.email')}</label>
              <Input
                type="email"
                value={contactInfo.email}
                onChange={(e) => setContactInfo(prev => ({ ...prev, email: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Working Hours */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Clock className="h-5 w-5 text-orange-500" />
            {t('contact.admin.working_hours_section')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.weekdays')}</label>
              <Input
                value={contactInfo.working_hours?.weekdays || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  working_hours: { ...prev.working_hours, weekdays: e.target.value }
                }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.saturday')}</label>
              <Input
                value={contactInfo.working_hours?.saturday || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  working_hours: { ...prev.working_hours, saturday: e.target.value }
                }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.sunday')}</label>
              <Input
                value={contactInfo.working_hours?.sunday || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  working_hours: { ...prev.working_hours, sunday: e.target.value }
                }))}
              />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Globe className="h-5 w-5 text-orange-500" />
            {t('contact.admin.social_media')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.facebook')}</label>
              <Input
                value={contactInfo.social?.facebook || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  social: { ...prev.social, facebook: e.target.value }
                }))}
                placeholder="https://facebook.com/..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('contact.admin.instagram')}</label>
              <Input
                value={contactInfo.social?.instagram || ''}
                onChange={(e) => setContactInfo(prev => ({ 
                  ...prev, 
                  social: { ...prev.social, instagram: e.target.value }
                }))}
                placeholder="https://instagram.com/..."
              />
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-orange-500" />
            {t('contact.admin.map_section')}
          </h2>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('contact.admin.latitude')}</label>
                <Input
                  value={contactInfo.map_coordinates?.lat || ''}
                  onChange={(e) => setContactInfo(prev => ({ 
                    ...prev, 
                    map_coordinates: { ...prev.map_coordinates, lat: e.target.value }
                  }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('contact.admin.longitude')}</label>
                <Input
                  value={contactInfo.map_coordinates?.lng || ''}
                  onChange={(e) => setContactInfo(prev => ({ 
                    ...prev, 
                    map_coordinates: { ...prev.map_coordinates, lng: e.target.value }
                  }))}
                />
              </div>
            </div>
            <p className="text-sm text-gray-500">
              {t('contact.admin.map_tip')}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <p className="text-sm text-blue-800">
          <strong>Info:</strong> {t('contact.admin.info_note')}
        </p>
      </div>
    </div>
  );
};

export default AdminContact;
